'use client';
import { useState } from 'react';
import { SmoothInput } from '@/components/ui/skiper-ui/skiper106';
import { ArrowRight, Check, Loader2 } from 'lucide-react';
import { CONTACT } from '@/content/offers';

const EMPTY = { projectType: '', description: '', features: '', budget: '', deadline: '', name: '', email: '', phone: '', website: '' };

const field = 'w-full rounded-md border border-input bg-card px-4 py-3 text-foreground placeholder:text-foreground/40 focus-visible:outline-2 focus-visible:outline-ring';
const label = 'mb-2 block text-sm font-medium text-foreground';

// Même logique que l'ancien formulaire (champs, validation, /api/contact). `website` est un champ piège : invisible, les robots le remplissent.
export default function ContactForm({ withIntro = true }: { withIntro?: boolean }) {
    const [data, setData] = useState(EMPTY);
    const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
    const [error, setError] = useState('');
    const cta = status === 'sending' ? 'Envoi en cours…' : status === 'success' ? 'Demande envoyée' : 'Lancer mon projet';

    const set = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setData({ ...data, [e.target.name]: e.target.value });

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        setStatus('sending');
        setError('');
        try {
            const res = await fetch('/api/contact', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) });
            const json = await res.json();
            if (!res.ok) throw new Error(json.error || 'Une erreur est survenue');
            setStatus('success');
            setData(EMPTY);
        } catch (err) {
            setStatus('error');
            setError(err instanceof Error ? err.message : 'Une erreur est survenue');
        }
    };

    return (
        <section id="contact" className="scroll-mt-24 border-t border-border px-6 py-24 md:py-32">
            <div className="mx-auto grid max-w-7xl gap-14 lg:grid-cols-[0.8fr_1.2fr]">
                <div>
                    {withIntro && (
                        <>
                            <h2 className="text-4xl font-semibold tracking-[-0.03em] md:text-6xl">Parlons de votre projet</h2>
                            <p className="mt-6 max-w-md text-lg leading-8 text-muted-foreground">
                                Un projet de site, d’application ou une mission à pourvoir : décrivez-le en quelques lignes. Je vous réponds sous 24 h.
                            </p>
                        </>
                    )}
                    <ul className="mt-10 space-y-3 text-base">
                        <li>
                            <a href={`mailto:${CONTACT.email}`} className="text-primary underline-offset-4 hover:underline">
                                {CONTACT.email}
                            </a>
                        </li>
                        <li>
                            <a href={CONTACT.phoneHref} className="text-primary underline-offset-4 hover:underline">
                                {CONTACT.phone}
                            </a>
                        </li>
                        <li className="text-muted-foreground">{CONTACT.city}, et à distance partout en France</li>
                    </ul>
                </div>

                <form onSubmit={submit} className="grid gap-6 sm:grid-cols-2">
                    <div className="sm:col-span-2">
                        <label htmlFor="projectType" className={label}>
                            Type de projet *
                        </label>
                        <select id="projectType" name="projectType" required value={data.projectType} onChange={set} className={field}>
                            <option value="">Choisissez</option>
                            <option value="vitrine">Site vitrine</option>
                            <option value="custom">Application web / sur mesure</option>
                            <option value="branding">Identité visuelle</option>
                            <option value="branding_vitrine">Site vitrine + identité visuelle</option>
                            <option value="mission">Mission en ESN / renfort d’équipe</option>
                        </select>
                    </div>
                    <div className="sm:col-span-2">
                        <label htmlFor="description" className={label}>
                            Votre projet *
                        </label>
                        <textarea id="description" name="description" required rows={5} value={data.description} onChange={set} className={field} placeholder="Votre activité, le projet ou la mission, vos délais…" />
                    </div>
                    <div className="sm:col-span-2">
                        <label htmlFor="features" className={label}>
                            Fonctionnalités souhaitées
                        </label>
                        <textarea id="features" name="features" rows={3} value={data.features} onChange={set} className={field} placeholder="Réservation, paiement, blog, espace membre…" />
                    </div>
                    <div>
                        <label htmlFor="budget" className={label}>
                            Budget
                        </label>
                        <select id="budget" name="budget" value={data.budget} onChange={set} className={field}>
                            <option value="">Je ne sais pas encore</option>
                            <option value="1000-2500">1 000 – 2 500 €</option>
                            <option value="2500-5000">2 500 – 5 000 €</option>
                            <option value="5000-10000">5 000 – 10 000 €</option>
                            <option value="10000+">Plus de 10 000 €</option>
                        </select>
                    </div>
                    <div>
                        <label htmlFor="deadline" className={label}>
                            Délai
                        </label>
                        <select id="deadline" name="deadline" value={data.deadline} onChange={set} className={field}>
                            <option value="">Pas de date précise</option>
                            <option value="urgent">Urgent (2 à 3 semaines)</option>
                            <option value="normal">Normal (1 à 2 mois)</option>
                            <option value="flexible">Flexible (3 mois et plus)</option>
                        </select>
                    </div>
                    <div>
                        <label htmlFor="name" className={label}>
                            Nom *
                        </label>
                        <SmoothInput id="name" name="name" required autoComplete="name" placeholder="Prénom Nom" value={data.name} onChange={set} />
                    </div>
                    <div>
                        <label htmlFor="email" className={label}>
                            Email *
                        </label>
                        <SmoothInput id="email" name="email" required inputMode="email" autoComplete="email" placeholder="vous@entreprise.fr" value={data.email} onChange={set} />
                    </div>
                    <div className="sm:col-span-2">
                        <label htmlFor="phone" className={label}>
                            Téléphone
                        </label>
                        <SmoothInput id="phone" name="phone" inputMode="tel" autoComplete="tel" placeholder="06 12 34 56 78" value={data.phone} onChange={set} />
                    </div>
                    {/* Champ piège anti-spam : caché aux humains et aux lecteurs d'écran */}
                    <div aria-hidden className="absolute -left-[9999px] h-0 w-0 overflow-hidden">
                        <label htmlFor="website">Site web</label>
                        <input id="website" name="website" tabIndex={-1} autoComplete="off" value={data.website} onChange={set} />
                    </div>

                    <div className="flex flex-wrap items-center gap-6 sm:col-span-2">
                        <button type="submit" className="btn-send" data-status={status} disabled={status === 'sending'}>
                            <span className="btn-send-label" data-text={cta}>
                                {cta}
                            </span>
                            <span className="btn-send-icon" aria-hidden>
                                {status === 'sending' ? <Loader2 className="animate-spin" /> : status === 'success' ? <Check /> : <ArrowRight />}
                            </span>
                        </button>
                        <p role="status" aria-live="polite" className="text-sm">
                            {status === 'success' && <span className="text-foreground">Merci, votre demande est bien partie. Je vous réponds sous 24 h.</span>}
                            {status === 'error' && (
                                <span className="text-destructive">
                                    {error}. Vous pouvez aussi m’écrire à {CONTACT.email}.
                                </span>
                            )}
                        </p>
                    </div>
                </form>
            </div>
        </section>
    );
}
